import React, { useState, useMemo, useRef, useCallback } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { getCategoryOrSeriesMap, findSeriesKeylistByKey } from "utils";
import { SearchInput } from "components/Inputs/SearchInput";
import { useClickOutside, useTranslate } from "hooks";
import {
  useProductsOverviewActions,
  useProductsOverviewStore,
} from "stores/productsOverviewStore";
import type { NiceModalHandler } from "@ebay/nice-modal-react";
import {
  productNavigationHandler,
  type ModalProps,
} from "utils/productNavigationHandler";
import { useAllProducts } from "hooks/productHooks/useAllProducts";
import { SearchResults, type SearchResultItem } from "./SearchResults";
import { useProject } from "stores/project";
import type { CategoryItem } from "utils/getCategoryOrSeriesMap";

import s from "./ProductsOverview.module.scss";

type ProductsOverviewSearchProps = {
  isModalActive: boolean;
  id?: string;
  dummy?: string;
  wizardType?: string;
  modal?: NiceModalHandler<Record<string, unknown>>;
  handleBackAction?: () => void;
};

export const ProductsOverviewSearch = Scrivito.connect(
  ({
    isModalActive,
    id,
    dummy,
    wizardType,
    modal,
    handleBackAction,
  }: ProductsOverviewSearchProps) => {
    const translate = useTranslate();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [query, setQuery] = useState("");
    const project = useProject();
    const allProducts = useAllProducts();
    const productCategoryId = useProductsOverviewStore(
      (state) => state.productCategoryId,
    );

    const {
      setProductCategoryId,
      setProductsContent,
      setCategoryHeadline,
      navigateToConfiguratorPage,
      navigateToProductListPage,
    } = useProductsOverviewActions();

    const productsContent = useProductsOverviewStore(
      (state) => state.productsContent,
    );
    const [categoryMap, seriesMap] =
      allProducts &&
      getCategoryOrSeriesMap(
        allProducts as Parameters<typeof getCategoryOrSeriesMap>[0],
      );

    const listOfSeries = useMemo(
      () => Array.from(seriesMap.values()),
      [seriesMap],
    );
    const listOfCategories = useMemo(
      () => Array.from(categoryMap.values()) as CategoryItem[],
      [categoryMap],
    );

    const findCategoryByName = (value: string): CategoryItem | null => {
      if (!value || !Array.isArray(listOfCategories)) {
        return null;
      }

      const lowerValue = value.toLowerCase();

      return (
        listOfCategories.find(
          (category) =>
            typeof category.name === "string" &&
            category.name.toLowerCase() === lowerValue,
        ) || null
      );
    };

    const handleClick = (item: SearchResultItem, isSerie?: boolean) => {
      productNavigationHandler({
        product: { ...item, isSerie: isSerie ?? false } as Parameters<
          typeof productNavigationHandler
        >[0]["product"],
        isModalActive,
        id: id ?? "",
        dummy: dummy ?? "",
        wizardType: wizardType ?? "",
        handleBackAction: handleBackAction ?? (() => undefined),
        modal: modal! as ModalProps,
        setProductCategoryId,
        navigateToConfiguratorPage,
        projectId: project?.id ?? "",
        navigateToProductListPage,
        setQuery,
        setCategoryHeadline,
        findSeriesKeylistByKey,
        seriesMap,
        productCategoryId,
        findCategoryByName: findCategoryByName as Parameters<
          typeof productNavigationHandler
        >[0]["findCategoryByName"],
        groupPosition: 0,
        type: "",
      });
    };

    const handleSearchInputChange = (
      e: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const value = e.target.value || "";
      setQuery(value);
      if (!value) {
        setProductsContent(productsContent);
      }
    };

    const handleClickOutsideDropdown = useCallback(() => {
      setQuery("");
    }, []);

    useClickOutside(dropdownRef, handleClickOutsideDropdown);

    return (
      <div className="d-flex flex-column" ref={dropdownRef}>
        <div className={cx(s.searchDiv)}>
          <SearchInput
            classNames={cx(s.productsOverviewSearch, {
              "bg-light": !isModalActive,
            })}
            placeHolder={translate("SEARCH_FIELD_PLACEHOLDER")}
            value={query}
            onChange={
              handleSearchInputChange as Parameters<
                typeof SearchInput
              >[0]["onChange"]
            }
          />
        </div>
        {query && (
          <div className={cx(s.searchDiv, s.searchResultsDiv)}>
            <div className={cx(s.searchResults, "p-2 mt-2")}>
              <div className="col-12">
                <SearchResults
                  isModalActive={isModalActive}
                  results={
                    (isModalActive
                      ? productsContent
                      : listOfSeries) as SearchResultItem[]
                  }
                  title={
                    translate("PRODUCT_SEARCH_SERIES_RESULTS_TITLE") as string
                  }
                  query={query}
                  handleClick={handleClick}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);
